// Country name to ISO 3166-1 alpha-2 code mapping for flags
export const COUNTRY_TO_FLAG: Record<string, string> = {
  // Major football nations
  "Argentina": "ar", "Brazil": "br", "France": "fr", "Germany": "de", "Spain": "es",
  "England": "gb-eng", "Italy": "it", "Portugal": "pt", "Netherlands": "nl", "Belgium": "be",
  "Croatia": "hr", "Uruguay": "uy", "Colombia": "co", "Mexico": "mx", "USA": "us",
  "United States": "us", "Canada": "ca", "Japan": "jp", "South Korea": "kr", "Australia": "au",
  "Morocco": "ma", "Senegal": "sn", "Ghana": "gh", "Nigeria": "ng", "Cameroon": "cm",
  "Egypt": "eg", "Algeria": "dz", "Tunisia": "tn", "South Africa": "za",
  "Poland": "pl", "Denmark": "dk", "Switzerland": "ch", "Austria": "at", "Sweden": "se",
  "Norway": "no", "Czech Republic": "cz", "Czechia": "cz", "Wales": "gb-wls", "Scotland": "gb-sct",
  "Ireland": "ie", "Northern Ireland": "gb-nir", "Serbia": "rs", "Ukraine": "ua", "Turkey": "tr",
  "Greece": "gr", "Romania": "ro", "Hungary": "hu", "Slovakia": "sk", "Slovenia": "si",
  "Ecuador": "ec", "Chile": "cl", "Peru": "pe", "Paraguay": "py", "Venezuela": "ve",
  "Bolivia": "bo", "Costa Rica": "cr", "Panama": "pa", "Honduras": "hn", "El Salvador": "sv",
  "Jamaica": "jm", "Haiti": "ht", "Trinidad and Tobago": "tt", "Curaçao": "cw", "Curacao": "cw",
  "Iran": "ir", "Saudi Arabia": "sa", "Qatar": "qa", "UAE": "ae", "United Arab Emirates": "ae",
  "Iraq": "iq", "Jordan": "jo", "Uzbekistan": "uz", "China": "cn", "India": "in",
  "Indonesia": "id", "Thailand": "th", "Vietnam": "vn", "Philippines": "ph", "Malaysia": "my",
  "New Zealand": "nz", "Russia": "ru", "Finland": "fi", "Iceland": "is",
  // Election-specific countries
  "Nepal": "np", "Singapore": "sg", "Bangladesh": "bd", "Sri Lanka": "lk", "Pakistan": "pk",
  "Afghanistan": "af", "Myanmar": "mm", "Cambodia": "kh", "Laos": "la", "Mongolia": "mn",
  "Taiwan": "tw", "Hong Kong": "hk", "North Korea": "kp",
  "Kenya": "ke", "Ethiopia": "et", "Tanzania": "tz", "Uganda": "ug", "Rwanda": "rw",
  "Ivory Coast": "ci", "Côte d'Ivoire": "ci", "Mali": "ml", "Burkina Faso": "bf",
  "Niger": "ne", "Chad": "td", "Congo": "cg", "DR Congo": "cd",
  "Democratic Republic of the Congo": "cd", "Angola": "ao", "Mozambique": "mz", "Zimbabwe": "zw",
  "Zambia": "zm", "Madagascar": "mg", "Somalia": "so", "Sudan": "sd", "Libya": "ly",
  "United Kingdom": "gb", "UK": "gb", "Lithuania": "lt", "Latvia": "lv", "Estonia": "ee",
  "Bulgaria": "bg", "Albania": "al", "North Macedonia": "mk", "Montenegro": "me",
  "Bosnia and Herzegovina": "ba", "Kosovo": "xk", "Moldova": "md", "Belarus": "by",
  "Georgia": "ge", "Armenia": "am", "Azerbaijan": "az", "Kazakhstan": "kz",
  "Kyrgyzstan": "kg", "Tajikistan": "tj", "Turkmenistan": "tm",
  "Israel": "il", "Lebanon": "lb", "Syria": "sy", "Yemen": "ye", "Oman": "om",
  "Kuwait": "kw", "Bahrain": "bh",
  "Cuba": "cu", "Dominican Republic": "do", "Puerto Rico": "pr", "Guatemala": "gt",
  "Nicaragua": "ni", "Belize": "bz", "Guyana": "gy", "Suriname": "sr",
  "Malta": "mt", "Luxembourg": "lu", "Cyprus": "cy", "Liechtenstein": "li", "Monaco": "mc",
  // World Cup name variants (Polymarket uses these exact strings) — 2026-06-10
  "Korea Republic": "kr", "Bosnia-Herzegovina": "ba", "Türkiye": "tr",
  "Cabo Verde": "cv", "Cape Verde": "cv", "New Caledonia": "nc", "Congo DR": "cd",
  "IR Iran": "ir",
};

// Accent/diacritic-insensitive normalization so "Türkiye"/"Curaçao" resolve.
const _normCountry = (s: string) =>
  s.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").trim();
const _FLAG_BY_NORM: Record<string, string> = (() => {
  const m: Record<string, string> = {};
  for (const [k, v] of Object.entries(COUNTRY_TO_FLAG)) m[_normCountry(k)] = v;
  return m;
})();

// Get flag URL for a country name (flagcdn.com — public domain). w320 (was
// w160) so flags aren't pixelated on retina. Normalized lookup as a fallback.
export function getCountryFlagUrl(countryName: string | null | undefined): string | null {
  if (!countryName) return null;
  const code = COUNTRY_TO_FLAG[countryName.trim()] || _FLAG_BY_NORM[_normCountry(countryName)];
  if (!code) return null;
  return `https://flagcdn.com/w320/${code}.png`;
}

// Per-nation primary colors — extracted from Polymarket's World Cup group
// standings so our "Advancing" bars match theirs exactly (2026-06-10).
export const COUNTRY_COLOR: Record<string, string> = {
  "Mexico": "#006847", "Czechia": "#11457e", "South Korea": "#1b2a4a", "South Africa": "#0a6b3e",
  "Switzerland": "#d52b1e", "Canada": "#c8102e", "Bosnia and Herzegovina": "#1c3f78", "Qatar": "#7a1235",
  "Brazil": "#179b4e", "Morocco": "#9a1b1e", "Scotland": "#0a3161", "Haiti": "#1c3f8f",
  "USA": "#1c2a52", "United States": "#1c2a52", "Türkiye": "#c8102e", "Paraguay": "#1c4a8a",
  "Australia": "#0a572e", "Germany": "#1a1a1a", "Ecuador": "#1c4a8a", "Ivory Coast": "#b35900",
  "Curaçao": "#1c3f8f", "Netherlands": "#c25e00", "Japan": "#bc002d", "Sweden": "#1c4a8a",
  "Tunisia": "#9a1b1e", "Belgium": "#111111", "Egypt": "#9a1b1e", "Iran": "#0a7b3e",
  "New Zealand": "#1a1a1a", "Spain": "#aa151b", "Uruguay": "#3b6cb3", "Saudi Arabia": "#046a38",
  "Cape Verde": "#1c3f8f", "Cabo Verde": "#1c3f8f", "France": "#1a2a6b", "Norway": "#ba0c2f",
  "Senegal": "#0a7b3e", "Iraq": "#0a6b3e", "Argentina": "#3d6fb4", "Austria": "#c8102e",
  "Algeria": "#0a6b3e", "Jordan": "#0a7b3e", "Portugal": "#046a38", "Colombia": "#b08600",
  "Congo DR": "#1c5fb0", "DR Congo": "#1c5fb0", "Uzbekistan": "#1c7a4d", "England": "#1d3461", "Croatia": "#b01e28",
  "Ghana": "#0a7b3e", "Panama": "#1c3f8f", "IR Iran": "#0a7b3e",
};
const _COLOR_BY_NORM: Record<string, string> = (() => {
  const m: Record<string, string> = {};
  for (const [k, v] of Object.entries(COUNTRY_COLOR)) m[_normCountry(k)] = v;
  return m;
})();
export function getCountryColor(countryName: string | null | undefined): string | null {
  if (!countryName) return null;
  return COUNTRY_COLOR[countryName.trim()] || _COLOR_BY_NORM[_normCountry(countryName)] || null;
}

// Polymarket uses a SEPARATE, more saturated palette for the moneyline buttons
// on the World Cup *games* page (distinct from the muted standings-bar colors
// above — e.g. Korea is navy in standings but red on game buttons). Extracted
// from polymarket.com/sports/world-cup/games (2026-06-10). Falls back to the
// standings color when a team's game color hasn't been captured yet.
export const WC_GAME_COLOR: Record<string, string> = {
  "Mexico": "#107051", "South Africa": "#10704b",
  "South Korea": "#cc2e3b", "Korea Republic": "#cc2e3b",
  "Czechia": "#cd1d23", "Czech Republic": "#cd1d23", "Canada": "#d62b1f",
  "Bosnia and Herzegovina": "#1b42c0", "Bosnia-Herzegovina": "#1b42c0",
  "USA": "#b31942", "United States": "#b31942", "Paraguay": "#d62b1f",
};
const _WC_GAME_BY_NORM: Record<string, string> = (() => {
  const m: Record<string, string> = {};
  for (const [k, v] of Object.entries(WC_GAME_COLOR)) m[_normCountry(k)] = v;
  return m;
})();
export function getWorldCupGameColor(countryName: string | null | undefined): string | null {
  if (!countryName) return null;
  return (
    WC_GAME_COLOR[countryName.trim()] ||
    _WC_GAME_BY_NORM[_normCountry(countryName)] ||
    getCountryColor(countryName)
  );
}

// FIFA/IOC 3-letter codes for the World Cup nations (Polymarket uses these on
// its game cards). A naive "first 3 letters" turns "United States" into "UNI"
// — wrong; this map fixes every participant. Keys cover the exact gamma
// spellings (incl. "Korea Republic", "IR Iran", "DR Congo", accented names).
export const WC_TEAM_ABBR: Record<string, string> = {
  "Argentina": "ARG", "Algeria": "ALG", "Australia": "AUS", "Austria": "AUT",
  "Belgium": "BEL", "Bosnia and Herzegovina": "BIH", "Bosnia-Herzegovina": "BIH",
  "Brazil": "BRA", "Cabo Verde": "CPV", "Cape Verde": "CPV", "Canada": "CAN",
  "Colombia": "COL", "Croatia": "CRO", "Côte d'Ivoire": "CIV", "Ivory Coast": "CIV",
  "Curaçao": "CUW", "Curacao": "CUW", "Czechia": "CZE", "Czech Republic": "CZE",
  "DR Congo": "COD", "Congo DR": "COD", "Ecuador": "ECU", "Egypt": "EGY",
  "England": "ENG", "France": "FRA", "Germany": "GER", "Ghana": "GHA",
  "Haiti": "HAI", "IR Iran": "IRN", "Iran": "IRN", "Iraq": "IRQ", "Japan": "JPN",
  "Jordan": "JOR", "Korea Republic": "KR", "South Korea": "KR", "Mexico": "MEX",
  "Morocco": "MAR", "Netherlands": "NED", "New Zealand": "NZL", "Norway": "NOR",
  "Panama": "PAN", "Paraguay": "PAR", "Portugal": "POR", "Qatar": "QAT",
  "Saudi Arabia": "KSA", "Scotland": "SCO", "Senegal": "SEN", "South Africa": "RSA",
  "Spain": "ESP", "Sweden": "SWE", "Switzerland": "SUI", "Tunisia": "TUN",
  "Türkiye": "TUR", "Turkey": "TUR", "United States": "USA", "USA": "USA",
  "Uruguay": "URU", "Uzbekistan": "UZB",
};
const _ABBR_BY_NORM: Record<string, string> = (() => {
  const m: Record<string, string> = {};
  for (const [k, v] of Object.entries(WC_TEAM_ABBR)) m[_normCountry(k)] = v;
  return m;
})();
export function getTeamAbbr(name: string | null | undefined): string {
  if (!name) return "";
  const hit = WC_TEAM_ABBR[name.trim()] || _ABBR_BY_NORM[_normCountry(name)];
  if (hit) return hit;
  return name.replace(/[^a-zA-Z]/g, "").slice(0, 3).toUpperCase() || name;
}

export interface CountryData {
  name: string;
  capital: string | null;
  population: number;
  region: string;
  subregion: string | null;
  cca2: string;
}

// In-memory cache — country data is static, never expires in session
const countryCache = new Map<string, CountryData | null>();

export async function getCountryData(countryName: string): Promise<CountryData | null> {
  const key = countryName.trim().toLowerCase();
  if (countryCache.has(key)) return countryCache.get(key) ?? null;

  try {
    const res = await fetch(
      `https://restcountries.com/v3.1/name/${encodeURIComponent(countryName.trim())}?fields=name,capital,population,region,subregion,cca2`,
    );
    if (!res.ok) {
      countryCache.set(key, null);
      return null;
    }
    const data = await res.json();
    if (!Array.isArray(data) || data.length === 0) {
      countryCache.set(key, null);
      return null;
    }
    const country = data[0];
    const result: CountryData = {
      name: country.name?.common ?? countryName,
      capital: Array.isArray(country.capital) ? country.capital[0] ?? null : null,
      population: country.population ?? 0,
      region: country.region ?? "",
      subregion: country.subregion ?? null,
      cca2: country.cca2 ?? "",
    };
    countryCache.set(key, result);
    return result;
  } catch {
    countryCache.set(key, null);
    return null;
  }
}

export function formatPopulation(n: number): string {
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1).replace(/\.0$/, "")}B`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
  return n.toString();
}

export function getCountdown(dateStr: string | null | undefined): string | null {
  if (!dateStr) return null;
  const target = new Date(dateStr);
  if (isNaN(target.getTime())) return null;

  const now = new Date();
  const diffMs = target.getTime() - now.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays < 0) return "Ended";
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Tomorrow";
  if (diffDays <= 90) return `In ${diffDays} days`;
  const months = Math.round(diffDays / 30);
  if (months <= 12) return `In ${months} months`;
  return `In ${Math.round(diffDays / 365)}+ years`;
}

export function formatVolume(n: number | undefined | null): string | null {
  if (n == null || n === 0) return null;
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1).replace(/\.0$/, "")}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
}
